import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from './api';
import { useAuth } from './auth';
import { Drawer, EmptyState, ErrorModal, Icon, StatusBadge } from './components';
import { AppAvatar } from './app-shell-core';
import { Link } from './router';

function permissionFor(me: ReturnType<typeof useAuth>['me'], organizationId: string, permission: string): boolean {
  if (!me) return false;
  if (['OWNER', 'ADMIN'].includes(me.user.platformRole)) return true;
  if (['SUPPORT', 'SECURITY', 'AUDITOR'].includes(me.user.platformRole)) return permission.endsWith('.view');
  return Boolean(me.memberships.find((item) => item.organization.id === organizationId)?.permissions.includes(permission));
}

export function communicationPatientName(patient: any): string {
  if (!patient) return 'Patient';
  return `${patient.preferredName || patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Patient';
}

function messageDateTime(value?: string | null): string {
  return value ? new Date(value).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
}

function compactTime(value?: string | null): string {
  return value ? new Date(value).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
}

function deliveryLabel(value?: string | null): string {
  if (!value) return '';
  return value.replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function channelLabel(value?: string | null): string {
  if (!value) return '';
  if (value === 'PORTAL') return 'Secure portal';
  if (value === 'EMAIL') return 'Email';
  if (value === 'SMS') return 'SMS';
  return deliveryLabel(value);
}

function currentThreadChannel(thread: any): 'PORTAL' | 'EMAIL' | 'SMS' | null {
  const messages = Array.isArray(thread?.messages) ? thread.messages : [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const channel = messages[index]?.channel;
    if (channel === 'PORTAL' || channel === 'EMAIL' || channel === 'SMS') return channel;
  }
  return null;
}

export function PatientConversationPanel({
  organizationId,
  threadId,
  onActivity,
  onBack,
  embedded = false,
}: {
  organizationId: string;
  threadId: string;
  onActivity?: () => void;
  onBack?: () => void;
  embedded?: boolean;
}) {
  const { me } = useAuth();
  const canManage = permissionFor(me, organizationId, 'communications.manage');
  const [thread, setThread] = useState<any>(null);
  const [body, setBody] = useState('');
  const [channel, setChannel] = useState('PORTAL');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const streamRef = useRef<HTMLDivElement | null>(null);

  const load = async () => {
    setError('');
    try {
      const result = await api<any>(`/organizations/${organizationId}/communications/threads/${threadId}`);
      setThread(result.thread);
      const preference = result.thread?.patient?.communicationPreference;
      const portalAvailable = (preference?.portalEnabled ?? true) && result.thread?.patient?.portalAccount?.status === 'ACTIVE';
      const emailAvailable = (preference?.emailEnabled ?? true) && Boolean(result.thread?.patient?.email);
      const smsAvailable = Boolean(result.thread?.smsProviderConfigured && preference?.smsEnabled);
      const conversationChannel = currentThreadChannel(result.thread);
      const nextChannel = conversationChannel === 'PORTAL' && portalAvailable ? 'PORTAL'
        : conversationChannel === 'EMAIL' && emailAvailable ? 'EMAIL'
        : conversationChannel === 'SMS' && smsAvailable ? 'SMS'
        : preference?.preferredChannel === 'PORTAL' && portalAvailable ? 'PORTAL'
        : preference?.preferredChannel === 'EMAIL' && emailAvailable ? 'EMAIL'
        : portalAvailable ? 'PORTAL'
        : emailAvailable ? 'EMAIL'
        : smsAvailable ? 'SMS'
        : 'PORTAL';
      setChannel(nextChannel);
      await api(`/organizations/${organizationId}/communications/threads/${threadId}/read`, { method: 'POST' });
      onActivity?.();
      window.dispatchEvent(new Event('nuramend:communications-changed'));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Conversation could not be loaded.');
    }
  };

  useEffect(() => { setThread(null); setBody(''); void load(); }, [organizationId, threadId]);
  useEffect(() => {
    if (!thread || !streamRef.current) return;
    streamRef.current.scrollTop = streamRef.current.scrollHeight;
  }, [thread?.messages?.length]);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!body.trim()) return;
    setBusy(true); setError('');
    try {
      await api(`/organizations/${organizationId}/communications/threads/${threadId}/messages`, { method: 'POST', body: JSON.stringify({ body, channel }) });
      setBody('');
      await load();
      onActivity?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Message could not be sent.');
    } finally { setBusy(false); }
  };

  const setThreadStatus = async (status: string) => {
    setBusy(true); setError('');
    try {
      await api(`/organizations/${organizationId}/communications/threads/${threadId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      await load();
      onActivity?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Conversation could not be updated.');
    } finally { setBusy(false); }
  };

  if (!thread) {
    return <section className={`messaging-conversation-panel ${embedded ? 'embedded' : ''}`.trim()}>
      {onBack && <button type="button" className="messaging-mobile-back" onClick={onBack}><Icon name="arrow-left" />Back to conversations</button>}
      {error ? <ErrorModal title="Conversation could not be opened" message={error} onClose={() => setError('')} /> : <div className="messaging-thread-loading"><span className="activity-icon"><Icon name="message" /></span><span><strong>Opening conversation…</strong><small>Loading secure message history.</small></span></div>}
    </section>;
  }

  const patient = thread.patient;
  const patientName = communicationPatientName(patient);
  const archived = thread.status === 'ARCHIVED';
  const closed = thread.status === 'CLOSED';
  const preference = patient.communicationPreference;
  const portalAvailable = (preference?.portalEnabled ?? true) && patient.portalAccount?.status === 'ACTIVE';
  const emailAvailable = (preference?.emailEnabled ?? true) && Boolean(patient.email);
  // SMS stays out of the active composer until a real provider integration reports it available.
  const smsAvailable = Boolean(thread.smsProviderConfigured && preference?.smsEnabled);

  return <section className={`messaging-conversation-panel ${embedded ? 'embedded' : ''}`.trim()}>
    {error && <ErrorModal title="Messaging action could not be completed" message={error} onClose={() => setError('')} />}
    {onBack && <button type="button" className="messaging-mobile-back" onClick={onBack}><Icon name="arrow-left" />Back to conversations</button>}
    <header className="messaging-conversation-header">
      <div className="messaging-conversation-person">
        <AppAvatar name={patientName} identityKey={patient.id || thread.id} size="md" />
        <span className="messaging-conversation-heading-copy">
          <span className="messaging-conversation-name-row"><strong>{patientName}</strong><StatusBadge value={thread.status} /></span>
          <b>{thread.subject}</b>
          <small>{patient.patientNumber}{patient.phone ? ` · ${patient.phone}` : ''}</small>
        </span>
      </div>
      <div className="messaging-conversation-header-actions">
        {!embedded && <Link className="button small" to={`/organizations/${organizationId}/patients/${patient.id}`}><Icon name="patients" />Open patient</Link>}
        {canManage && <details className="app-action-menu messaging-thread-more">
          <summary className="icon-button" aria-label="Conversation actions" title="Conversation actions"><Icon name="more" /></summary>
          <div className="app-action-menu-popover messaging-thread-more-popover">
            {closed && <button type="button" onClick={() => void setThreadStatus('OPEN')}><Icon name="refresh" />Reopen conversation</button>}
            {!closed && !archived && <button type="button" onClick={() => void setThreadStatus('CLOSED')}><Icon name="check" />Close conversation</button>}
            {!archived && <button type="button" onClick={() => void setThreadStatus('ARCHIVED')}><Icon name="archive" />Archive</button>}
            {archived && <button type="button" onClick={() => void setThreadStatus('OPEN')}><Icon name="refresh" />Restore conversation</button>}
          </div>
        </details>}
      </div>
    </header>

    <div className="messaging-conversation-context patient-conversation-context">
      <div className="messaging-conversation-context-main"><span><Icon name="security" />Patient conversation</span>{thread.priority === 'URGENT' ? <span className="messaging-priority-flag urgent" title="Urgent priority" aria-label="Urgent priority"><Icon name="flag" /><span>Urgent</span></span> : <StatusBadge value={thread.priority} />}{thread.assignedMembership && <span><Icon name="user-check" />{thread.assignedMembership.user.firstName} {thread.assignedMembership.user.lastName}</span>}{thread.appointment && <span><Icon name="calendar" />{compactTime(thread.appointment.startsAt)} · {thread.appointment.serviceType.name}</span>}</div>
      {canManage && !archived && <div className="messaging-delivery-radios" role="radiogroup" aria-label="Delivery"><span className="messaging-delivery-label">Delivery</span><label className={`${channel === 'PORTAL' ? 'selected ' : ''}${!portalAvailable ? 'disabled' : ''}`.trim()}><input type="radio" name={`delivery-${threadId}`} value="PORTAL" checked={channel === 'PORTAL'} disabled={!portalAvailable} onChange={() => setChannel('PORTAL')} /><span>Secure portal</span></label><label className={`${channel === 'EMAIL' ? 'selected ' : ''}${!emailAvailable ? 'disabled' : ''}`.trim()}><input type="radio" name={`delivery-${threadId}`} value="EMAIL" checked={channel === 'EMAIL'} disabled={!emailAvailable} onChange={() => setChannel('EMAIL')} /><span>Email</span></label>{smsAvailable && <label className={channel === 'SMS' ? 'selected' : ''}><input type="radio" name={`delivery-${threadId}`} value="SMS" checked={channel === 'SMS'} onChange={() => setChannel('SMS')} /><span>SMS</span></label>}</div>}
    </div>

    <div className="messaging-message-stream" ref={streamRef}>
      {thread.messages.map((message: any) => {
        const fromPatient = message.authorType === 'PATIENT';
        const author = fromPatient ? patientName : message.authorUser ? `${message.authorUser.firstName} ${message.authorUser.lastName}` : 'Nuramend';
        return <article key={message.id} className={`messaging-bubble ${fromPatient ? 'from-patient' : message.authorType === 'SYSTEM' ? 'from-system' : 'from-staff'}`}>
          <header><strong>{author}</strong><time>{messageDateTime(message.createdAt)}</time></header>
          <p>{message.body}</p>
          <footer><span>{channelLabel(message.channel)}</span>{!fromPatient && message.deliveryStatus && <span>{deliveryLabel(message.deliveryStatus)}</span>}</footer>
        </article>;
      })}
      {!thread.messages.length && <EmptyState title="No messages yet" body="Start the conversation from the composer below." />}
    </div>

    {canManage && !archived ? <form className="messaging-reply-composer" onSubmit={send}>
      <textarea aria-label="Reply" rows={3} value={body} placeholder={closed ? 'Replying will reopen this conversation…' : `Message ${patient.preferredName || patient.firstName}…`} onChange={(event) => setBody(event.target.value)} />
      <div className="messaging-composer-controls messaging-composer-send-row">
        <span className="messaging-secure-note"><Icon name="security" />{channel === 'EMAIL' ? 'Email delivery selected' : channel === 'SMS' ? 'SMS delivery selected' : 'Secure portal delivery selected'}</span>
        <button type="submit" className="button primary" disabled={busy || !body.trim() || (channel === 'PORTAL' && !portalAvailable) || (channel === 'EMAIL' && !emailAvailable)}><Icon name="send" />{busy ? 'Sending…' : 'Send'}</button>
      </div>
    </form> : <div className="messaging-thread-locked"><Icon name={archived ? 'archive' : 'security'} /><span><strong>{archived ? 'Conversation archived' : 'Read-only access'}</strong><small>{archived ? 'Restore it from the conversation menu to send another message.' : 'Your role can review this conversation but cannot send replies.'}</small></span></div>}
  </section>;
}

export function TeamConversationPanel({
  organizationId,
  threadId,
  onActivity,
  onBack,
}: {
  organizationId: string;
  threadId: string;
  onActivity?: () => void;
  onBack?: () => void;
}) {
  const { me } = useAuth();
  const canManage = permissionFor(me, organizationId, 'communications.manage');
  const [thread, setThread] = useState<any>(null);
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const streamRef = useRef<HTMLDivElement | null>(null);

  const load = async () => {
    setError('');
    try {
      const result = await api<any>(`/organizations/${organizationId}/communications/collaboration/threads/${threadId}`);
      setThread(result.thread);
      await api(`/organizations/${organizationId}/communications/collaboration/threads/${threadId}/read`, { method: 'POST' });
      onActivity?.();
      window.dispatchEvent(new Event('nuramend:communications-changed'));
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Team conversation could not load.'); }
  };
  useEffect(() => { setThread(null); setBody(''); void load(); }, [organizationId, threadId]);
  useEffect(() => {
    if (!thread || !streamRef.current) return;
    streamRef.current.scrollTop = streamRef.current.scrollHeight;
  }, [thread?.messages?.length]);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!body.trim()) return;
    setBusy(true); setError('');
    try {
      await api(`/organizations/${organizationId}/communications/collaboration/threads/${threadId}/messages`, { method: 'POST', body: JSON.stringify({ body }) });
      setBody(''); await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Reply could not be added.'); }
    finally { setBusy(false); }
  };

  const setStatus = async (status: string) => {
    setBusy(true); setError('');
    try {
      await api(`/organizations/${organizationId}/communications/collaboration/threads/${threadId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Team conversation could not be updated.'); }
    finally { setBusy(false); }
  };

  if (!thread) return <section className="messaging-conversation-panel team-conversation-panel">{onBack && <button type="button" className="messaging-mobile-back" onClick={onBack}><Icon name="arrow-left" />Back to conversations</button>}{error ? <ErrorModal title="Team conversation could not be opened" message={error} onClose={() => setError('')} /> : <div className="messaging-thread-loading"><span className="activity-icon"><Icon name="users" /></span><span><strong>Opening team conversation…</strong><small>Loading message history.</small></span></div>}</section>;

  const recipients = thread.recipients?.map((recipient: any) => `${recipient.membership.user.firstName} ${recipient.membership.user.lastName}`) || [];
  const title = recipients.length ? recipients.slice(0, 3).join(', ') : thread.assignedMembership ? `${thread.assignedMembership.user.firstName} ${thread.assignedMembership.user.lastName}` : 'Team';
  const archived = thread.status === 'ARCHIVED';
  const resolved = thread.status === 'RESOLVED';

  return <section className="messaging-conversation-panel team-conversation-panel">
    {error && <ErrorModal title="Messaging action could not be completed" message={error} onClose={() => setError('')} />}
    {onBack && <button type="button" className="messaging-mobile-back" onClick={onBack}><Icon name="arrow-left" />Back to conversations</button>}
    <header className="messaging-conversation-header">
      <div className="messaging-conversation-person">
        <AppAvatar name={title} identityKey={thread.id} size="md" />
        <span className="messaging-conversation-heading-copy">
          <span className="messaging-conversation-name-row"><strong>{title}</strong><StatusBadge value={thread.status} /></span>
          <b>{thread.subject}</b>
          <small>Started by {thread.createdBy.firstName} {thread.createdBy.lastName}{recipients.length > 3 ? ` · +${recipients.length - 3} more` : ''}</small>
        </span>
      </div>
      <div className="messaging-conversation-header-actions">
        {thread.patient && <Link className="button small" to={`/organizations/${organizationId}/patients/${thread.patient.id}`}><Icon name="patients" />Open patient</Link>}
        {canManage && <details className="app-action-menu messaging-thread-more">
          <summary className="icon-button" aria-label="Conversation actions" title="Conversation actions"><Icon name="more" /></summary>
          <div className="app-action-menu-popover messaging-thread-more-popover">
            {!resolved && !archived && <button type="button" onClick={() => void setStatus('RESOLVED')}><Icon name="check" />Resolve</button>}
            {resolved && <button type="button" onClick={() => void setStatus('OPEN')}><Icon name="refresh" />Reopen</button>}
            {!archived && <button type="button" onClick={() => void setStatus('ARCHIVED')}><Icon name="archive" />Archive</button>}
            {archived && <button type="button" onClick={() => void setStatus('OPEN')}><Icon name="refresh" />Restore</button>}
          </div>
        </details>}
      </div>
    </header>

    <div className="messaging-conversation-context">
      <span><Icon name="users" />Team conversation</span>{thread.priority === 'URGENT' ? <span className="messaging-priority-flag urgent" title="Urgent priority" aria-label="Urgent priority"><Icon name="flag" /><span>Urgent</span></span> : <StatusBadge value={thread.priority} />}
      {thread.assignedMembership && <span><Icon name="user-check" />Owner: {thread.assignedMembership.user.firstName} {thread.assignedMembership.user.lastName}</span>}
      {thread.patient && <span><Icon name="patients" />{communicationPatientName(thread.patient)} · {thread.patient.patientNumber}</span>}
      {thread.appointment && <span><Icon name="calendar" />{compactTime(thread.appointment.startsAt)} · {thread.appointment.serviceType.name}</span>}
    </div>

    <div className="messaging-message-stream team-message-stream" ref={streamRef}>
      {thread.messages.map((message: any) => {
        const mine = Boolean(me?.user?.id && message.authorUser?.id === me.user.id);
        return <article className={`messaging-bubble team-message ${mine ? 'from-staff' : 'from-patient'}`} key={message.id}>
          <header><strong>{message.authorUser.firstName} {message.authorUser.lastName}</strong><time>{messageDateTime(message.createdAt)}</time></header>
          <p>{message.body}</p>
        </article>;
      })}
    </div>

    {canManage && !archived ? <form className="messaging-reply-composer team-reply-composer" onSubmit={send}>
      <textarea aria-label="Team reply" rows={3} value={body} placeholder={resolved ? 'Replying will reopen this conversation…' : 'Reply to the team…'} onChange={(event) => setBody(event.target.value)} />
      <div className="messaging-composer-controls team-composer-controls"><span className="messaging-secure-note"><Icon name="security" />Internal to your Nuramend team</span><button type="submit" className="button primary" disabled={busy || !body.trim()}><Icon name="send" />{busy ? 'Sending…' : 'Reply'}</button></div>
    </form> : <div className="messaging-thread-locked"><Icon name={archived ? 'archive' : 'security'} /><span><strong>{archived ? 'Conversation archived' : 'Read-only access'}</strong><small>{archived ? 'Restore it to continue the discussion.' : 'Your role can review this conversation but cannot send replies.'}</small></span></div>}
  </section>;
}

export function PatientMessagingDrawer({
  organizationId,
  patient,
  initialThreadId = '',
  onClose,
  onActivity,
}: {
  organizationId: string;
  patient: any;
  initialThreadId?: string;
  onClose: () => void;
  onActivity?: () => void;
}) {
  const { me } = useAuth();
  const canManage = permissionFor(me, organizationId, 'communications.manage');
  const [threads, setThreads] = useState<any[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState(initialThreadId);
  const [view, setView] = useState<'compose' | 'thread'>(initialThreadId ? 'thread' : 'compose');
  const [form, setForm] = useState({ subject: '', body: '', channel: 'PORTAL', priority: 'NORMAL' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const patientName = communicationPatientName(patient);

  const loadThreads = async () => {
    try {
      const params = new URLSearchParams({ patientId: patient.id, page: '1', pageSize: '12' });
      const result = await api<any>(`/organizations/${organizationId}/communications/threads?${params}`);
      setThreads(result.threads || []);
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Recent patient conversations could not be loaded.'); }
  };
  useEffect(() => { void loadThreads(); }, [organizationId, patient.id]);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) return;
    setBusy(true); setError('');
    try {
      const result = await api<any>(`/organizations/${organizationId}/communications/threads`, { method: 'POST', body: JSON.stringify({ ...form, patientId: patient.id }) });
      setForm({ subject: '', body: '', channel: 'PORTAL', priority: 'NORMAL' });
      setSelectedThreadId(result.thread.id);
      setView('thread');
      await loadThreads();
      onActivity?.();
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Patient message could not be sent.'); }
    finally { setBusy(false); }
  };

  const openThread = (threadId: string) => { setSelectedThreadId(threadId); setView('thread'); };
  const startNew = () => { setSelectedThreadId(''); setView('compose'); };

  return <Drawer title="Patient messages" icon="message" onClose={onClose} className="app-quickview-drawer patient-messaging-drawer">
    <div className="patient-messaging-drawer-shell">
      {error && <ErrorModal title="Messaging action could not be completed" message={error} onClose={() => setError('')} />}
      <section className="patient-messaging-drawer-hero">
        <AppAvatar name={patientName} identityKey={patient.id} size="md" />
        <span><strong>{patientName}</strong><small>{patient.patientNumber}{patient.email ? ` · ${patient.email}` : ''}</small></span>
      </section>
      <div className="patient-messaging-drawer-toolbar">
        {canManage && <button type="button" className={`button small ${view === 'compose' ? 'primary' : ''}`.trim()} onClick={startNew}><Icon name="edit" />New message</button>}
        <Link className="button small" to={`/organizations/${organizationId}/communications?mode=patient&patientId=${encodeURIComponent(patient.id)}`} onClick={onClose}><Icon name="inbox" />View all messages</Link>
      </div>

      {view === 'thread' && selectedThreadId ? <div className="patient-messaging-thread-view">
        <button type="button" className="icon-text-link patient-messaging-back" onClick={startNew}><Icon name="arrow-left" />{canManage ? 'New message & recent conversations' : 'Recent conversations'}</button>
        <PatientConversationPanel organizationId={organizationId} threadId={selectedThreadId} embedded onActivity={() => { void loadThreads(); onActivity?.(); }} />
      </div> : <div className="patient-messaging-drawer-content">
        {canManage && <section className="patient-message-compose-panel">
          <header><span><Icon name="send" /></span><div><h3>New secure message</h3><p>Send from the patient record without leaving the workspace.</p></div></header>
          <form className="patient-message-drawer-form" onSubmit={create}>
            <label>Subject<input required maxLength={191} value={form.subject} placeholder="What is this message about?" onChange={(event) => setForm({ ...form, subject: event.target.value })} /></label>
            <label>Message<textarea required rows={5} value={form.body} placeholder={`Message ${patient.preferredName || patient.firstName}…`} onChange={(event) => setForm({ ...form, body: event.target.value })} /></label>
            <div className="patient-message-drawer-options">
              <label><span>Delivery</span><select value={form.channel} onChange={(event) => setForm({ ...form, channel: event.target.value })}><option value="PORTAL">Secure portal</option><option value="EMAIL">Email</option></select></label>
              <label><span>Priority</span><select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })}><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option></select></label>
              <button type="submit" className="button primary" disabled={busy || form.subject.trim().length < 2 || !form.body.trim()}><Icon name="send" />{busy ? 'Sending…' : 'Send message'}</button>
            </div>
          </form>
        </section>}

        <section className="patient-message-recent-panel">
          <header><div><h3>Recent conversations</h3><p>Open a thread and reply without leaving this patient record.</p></div><span>{threads.length}</span></header>
          {threads.length ? <div className="patient-message-recent-list">{threads.map((thread) => <button type="button" key={thread.id} onClick={() => openThread(thread.id)} className={thread.unread ? 'unread' : ''}>
            <span className="patient-message-recent-main"><strong>{thread.subject}</strong><small>{thread.messages?.[0]?.body || 'No message preview'}</small></span>
            <span className="patient-message-recent-meta"><StatusBadge value={thread.status} /><time>{compactTime(thread.lastMessageAt)}</time></span>
          </button>)}</div> : <div className="patient-message-drawer-empty"><Icon name="message" /><span><strong>No conversations yet</strong><small>The first message you send will start the patient’s secure conversation history.</small></span></div>}
        </section>
      </div>}
    </div>
  </Drawer>;
}
